/* eslint-disable @typescript-eslint/consistent-type-assertions */
import * as React from 'react'
import { Temporal } from '@js-temporal/polyfill'
import { clsx } from 'clsx'
import { Time } from '@internationalized/date'
import { useDateSegment, useTimeField } from 'react-aria'
import type { TimeValue, DateValue } from 'react-aria'
import type { DateFieldState, DateSegment } from 'react-stately'
import { useTimeFieldState } from 'react-stately'

import { formatPlainTemporal } from '../../helpers/timeZone'
import { useMergeRefs } from '../../hooks/useMergeRefs'
import { DropdownMenu } from '../DropdownMenu'
import type { DropdownMenuContentProps } from '../DropdownMenu'
import { ScrollArea } from '../ScrollArea'
import { SmartTimeInput } from '../SmartTimeInput'
import { WarningIcon } from '../icons'

import styles from './TimeInput.module.css'
import { Flex } from '../Flex'
import { Text } from '../Text'
import { Tooltip } from '../Tooltip'

type HandleInteractOutsideEvent =
  NonNullable<DropdownMenuContentProps['onInteractOutside']> extends (
    event: infer E,
  ) => void
    ? E
    : never

/**
 * Converts a Temporal.PlainTime object to a TimeValue object for React Aria
 *
 * @param plainTime - The Temporal.PlainTime object to convert
 * @returns A TimeValue object compatible with React Aria, or undefined if no input is provided
 */
function plainTimeToTimeValue(
  plainTime?: Temporal.PlainTime,
): Time | undefined {
  if (!plainTime) return undefined

  return new Time(plainTime.hour, plainTime.minute, plainTime.second)
}

/**
 * Converts a React Aria TimeValue object to a Temporal.PlainTime object
 *
 * @param timeValue - The TimeValue object to convert
 * @returns A Temporal.PlainTime object, or null if no input is provided
 */
function timeValueToPlainTime(
  timeValue?: TimeValue,
): Temporal.PlainTime | null {
  if (!timeValue) return null

  return new Temporal.PlainTime(
    timeValue.hour ?? 0,
    timeValue.minute ?? 0,
    timeValue.second ?? 0,
  )
}

/**
 * Checks if a pointer event overlaps with a given HTML element.
 *
 * This helper is used to determine if a click event occurred within the bounds
 * of a specific element, typically to prevent dropdown closure when clicking
 * the trigger element.
 *
 * @param e - The interact outside event from Radix UI's dismissable layer
 * @param ref - The HTML element to check overlap against
 * @returns True if the pointer event coordinates overlap the element's bounds
 */
function isEventOverlappingRef(
  e: HandleInteractOutsideEvent,
  ref: HTMLElement | null,
): boolean {
  if (!ref) return false
  const triggerRect = ref.getBoundingClientRect()

  // Only handle pointer events, not focus events
  if (e.type !== 'dismissableLayer.pointerDownOutside') {
    return false
  }

  const pointerEvent = e.detail.originalEvent as PointerEvent
  const pointerCoordinates = {
    x: pointerEvent.clientX,
    y: pointerEvent.clientY,
  }
  return (
    pointerCoordinates.x > triggerRect.x &&
    pointerCoordinates.y > triggerRect.y &&
    pointerCoordinates.x < triggerRect.x + triggerRect.width &&
    pointerCoordinates.y < triggerRect.y + triggerRect.height
  )
}

/**
 * Props for the TimeSegment component.
 *
 * TimeSegment represents an individual editable portion of the time input
 * (e.g., hour, minute, second, or AM/PM period).
 */
interface TimeSegmentProps {
  /** The date segment data from React Aria */
  segment: DateSegment
  /** The time field state from React Aria */
  state: DateFieldState
  /** Optional keyboard event handler for custom behavior */
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>
  /** Optional ref to track the currently focused segment */
  focusRef?: React.MutableRefObject<HTMLDivElement | null>
}

/**
 * Props for the ConditionalTooltip component.
 */
interface ConditionalTooltipProps {
  children: React.ReactNode
  tooltipContent?: string
  align?: 'start' | 'center' | 'end'
}

/**
 * A conditional wrapper that shows a tooltip only when specified.
 * Used to display tooltips for time options in the dropdown.
 */
const ConditionalTooltip: React.FC<ConditionalTooltipProps> = ({
  children,
  tooltipContent,
  align = 'start',
}) => {
  if (!tooltipContent) {
    return <>{children}</>
  }
  return (
    <Tooltip align={align} delay={0} content={tooltipContent}>
      {children}
    </Tooltip>
  )
}

/**
 * Individual time segment component (hour, minute, second, etc.).
 *
 * Renders a single editable segment of the time input using React Aria's
 * date segment functionality. Each segment can be independently focused
 * and edited using keyboard input.
 *
 * @param segment - The segment configuration from React Aria
 * @param state - The time field state
 * @param onKeyDown - Optional keyboard event handler
 * @param focusRef - Optional ref to track focused segment
 * @returns A div element representing the time segment
 */
function TimeSegment({
  segment,
  state,
  onKeyDown,
  focusRef,
}: TimeSegmentProps): React.ReactElement {
  const ref = React.useRef<HTMLDivElement>(null)
  const { segmentProps } = useDateSegment(segment, state, ref)
  return (
    <div
      {...segmentProps}
      ref={ref}
      className={clsx(
        styles.segment,
        segment.type === 'hour' && styles.hour,
        segment.type === 'literal' && styles.literal,
        segment.isPlaceholder && styles.placeholder,
      )}
      style={{
        ...segmentProps.style,
      }}
      onKeyDown={(e) => {
        segmentProps.onKeyDown?.(e)
        if (onKeyDown) {
          onKeyDown(e)
        }
      }}
      onFocus={(e) => {
        segmentProps.onFocus?.(e)
        if (segment.type !== 'literal' && focusRef) {
          focusRef.current = ref.current
        }
      }}
      role={segment.type !== 'literal' ? 'spinbutton' : undefined}
    >
      {segment.text}
    </div>
  )
}

/**
 * Represents a time option with optional visual styling.
 *
 * This is the public interface for passing time options to TimeInput.
 * Used for times that should have special visual treatment (like warning icons)
 * but remain selectable.
 *
 * @deprecated Use TimeOverride instead - it combines both disabled and visual styling options
 */
export interface TimeOption {
  /** The time value */
  time: Temporal.PlainTime
  /** Visual variant for the time option */
  variant?: 'warning'
  /** Optional custom message shown in tooltip */
  tooltipText?: string
}

/**
 * Represents a time that should be disabled or hidden from the time picker.
 *
 * @deprecated Use TimeOverride instead - it combines both disabled and visual styling options
 */
export interface DisabledTime {
  /** The time to disable or remove */
  time: Temporal.PlainTime
  /** Optional custom message shown in tooltip (defaults to "Time not available") */
  reason?: string
  /** If true, the time option is completely hidden from the dropdown (default: false) */
  remove?: boolean
}

/**
 * Represents an override for a specific time in the TimeInput dropdown.
 *
 * This unified interface allows you to modify the behavior and appearance of specific times
 * by combining disabling functionality with visual styling in a single configuration.
 *
 * @example
 * // Disable a time with a custom tooltip
 * { time: new Temporal.PlainTime(12, 0), disabled: true, tooltipText: "Lunch break" }
 *
 * @example
 * // Show a warning but keep it selectable
 * { time: new Temporal.PlainTime(14, 30), variant: 'warning', tooltipText: 'Already scheduled' }
 *
 * @example
 * // Remove a time completely from the dropdown
 * { time: new Temporal.PlainTime(17, 0), remove: true }
 *
 * @example
 * // Combine: show warning and disable
 * { time: new Temporal.PlainTime(10, 0), disabled: true, variant: 'warning', tooltipText: 'Unavailable' }
 */
export interface TimeOverride {
  /** The time to override */
  time: Temporal.PlainTime

  /** Whether this time should be disabled (unselectable) */
  disabled?: boolean
  /** If true, completely hide this time from the dropdown */
  remove?: boolean

  /** Visual variant for the time option (e.g., warning icon) */
  variant?: 'warning'
  /** Tooltip text to display (works for both disabled and enabled items) */
  tooltipText?: string
}

/**
 * Internal time option type with computed label.
 * Extends TimeOverride with the computed label field.
 */
interface TimeOptionWithLabel extends TimeOverride {
  /** Formatted time string (e.g., "9:00 AM" or "14:00") */
  label: string
}

/**
 * Converts a Temporal.PlainTime to a formatted time label string.
 *
 * Handles both 12-hour and 24-hour formats with proper handling of edge cases
 * like midnight (12:00 AM) and noon (12:00 PM).
 *
 * @param time - The time to format
 * @param hourCycle - The hour format (12 or 24)
 * @returns Formatted time string (e.g., "9:00 AM" or "09:00")
 *
 * @example
 * toTimeLabel(new Temporal.PlainTime(9, 30), 12) // Returns: "9:30 AM"
 * toTimeLabel(new Temporal.PlainTime(14, 30), 24) // Returns: "14:30"
 * toTimeLabel(new Temporal.PlainTime(0, 0), 12) // Returns: "12:00 AM"
 */
export const toTimeLabel = (
  time: Temporal.PlainTime,
  hourCycle: 12 | 24,
): string => {
  if (hourCycle === 24) {
    return formatPlainTemporal(time, 'en-US', {
      hourCycle: 'h23',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Custom 12-hour format with 00:00 AM for midnight
  const hour = time.hour
  const minute = time.minute

  let displayHour: string
  let period: string

  if (hour === 0) {
    // Midnight: 12:00 AM
    displayHour = '12'
    period = 'AM'
  } else if (hour === 12) {
    // Noon: 12:00 PM
    displayHour = '12'
    period = 'PM'
  } else if (hour < 12) {
    // Morning: 1:00 AM - 11:00 AM
    displayHour = hour.toString()
    period = 'AM'
  } else {
    // Afternoon/Evening: 1:00 PM - 11:00 PM
    displayHour = (hour - 12).toString()
    period = 'PM'
  }

  const displayMinute = minute.toString().padStart(2, '0')

  return `${displayHour}:${displayMinute} ${period}`
}

/**
 * Generates time options for the dropdown menu.
 *
 * Creates a list of selectable time options based on the specified granularity
 * and constraints. Options are generated at 15-minute intervals (or 1-hour for
 * hour granularity) and filtered by min/max boundaries.
 *
 * @param hourCycle - The hour format (12 or 24)
 * @param granularity - Time granularity (hour or minute)
 * @param min - Minimum allowed time (times before this will be filtered out)
 * @param max - Maximum allowed time (times after this will be filtered out)
 * @param timeOverrides - Optional array of time overrides for custom behavior or styling
 * @returns Array of time options for the dropdown
 *
 * @example
 * const options = generateTimeOptions(12, 'minute', undefined, undefined, [
 *   { time: new Temporal.PlainTime(12, 0), disabled: true, tooltipText: "Lunch break" },
 *   { time: new Temporal.PlainTime(14, 30), remove: true }
 * ])
 */
function generateTimeOptions(
  hourCycle: 12 | 24,
  granularity: 'hour' | 'minute' = 'minute',
  min?: Temporal.PlainTime,
  max?: Temporal.PlainTime,
  timeOverrides: TimeOverride[] = [],
): TimeOptionWithLabel[] {
  const options: TimeOptionWithLabel[] = []
  const step = granularity === 'hour' ? 60 : 15

  // Create map for quick lookup of overrides
  const overridesMap = new Map<string, TimeOverride>()
  timeOverrides.forEach((override) => {
    const key = `${override.time.hour}:${override.time.minute}`
    overridesMap.set(key, override)
  })

  // Generate regular time slots
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += step) {
      if (granularity === 'hour' && m !== 0) continue

      const time = new Temporal.PlainTime(h, m)
      const timeKey = `${h}:${m}`
      const override = overridesMap.get(timeKey)

      // Skip times that should be completely removed
      if (override?.remove === true) {
        continue
      }

      options.push({
        time,
        label: toTimeLabel(time, hourCycle),
        disabled: override?.disabled,
        variant: override?.variant,
        tooltipText: override?.tooltipText,
      })
    }
  }

  // Apply min/max filtering
  const filteredOptions = options.filter((opt) => {
    if (min && Temporal.PlainTime.compare(opt.time, min) < 0) return false
    if (max && Temporal.PlainTime.compare(opt.time, max) > 0) return false

    return true
  })

  // Sort by time (hour, then minute)
  filteredOptions.sort((a, b) => Temporal.PlainTime.compare(a.time, b.time))

  return filteredOptions
}

/**
 * Props for the TimeInput component.
 *
 * TimeInput can be used in controlled or uncontrolled mode:
 * - **Controlled**: Provide both `value` and `onChange`
 * - **Uncontrolled**: Provide only `defaultValue`
 */
export interface TimeInputProps {
  /**
   * The type of time input to render.
   *
   * - `'classic'` (default): Segment-based time input with individual editable
   *   segments for hour, minute, and AM/PM.
   * - `'smart'` **(Alpha)**: Natural language time input that accepts typed expressions
   *   like "2pm", "noon", "quarter past 3", etc. Powered by `SmartTimeInput` internally.
   *
   * @default 'classic'
   */
  type?: 'classic' | 'smart'
  /**
   * The current value (controlled mode).
   *
   * When provided, the component operates in controlled mode.
   * You must also provide `onChange` to handle updates.
   *
   * Uses the Temporal API PlainTime object from @js-temporal/polyfill.
   *
   * @example
   * const [time, setTime] = useState<Temporal.PlainTime>()
   * <TimeInput value={time} onChange={setTime} />
   */
  value?: Temporal.PlainTime
  /**
   * The default value (uncontrolled mode).
   *
   * Sets the initial value when the component is not controlled.
   * The component manages its own state internally.
   *
   * Uses the Temporal API PlainTime object from @js-temporal/polyfill.
   *
   * @example
   * <TimeInput defaultValue={new Temporal.PlainTime(9, 0)} />
   */
  defaultValue?: Temporal.PlainTime
  /**
   * Handler called when the time value changes.
   *
   * Receives the new PlainTime value or null if the time is cleared.
   *
   * @param value - The new time value or null
   *
   * @example
   * <TimeInput onChange={(time) => console.log(time?.toString())} />
   */
  onChange?: (value: Temporal.PlainTime | null) => void
  /**
   * Whether the time field is disabled.
   *
   * Disabled fields are non-interactive and visually dimmed.
   */
  disabled?: boolean
  /**
   * Whether the time field is read-only.
   *
   * Read-only fields display the value but prevent editing.
   */
  readOnly?: boolean
  /**
   * Whether the time field is required.
   *
   * Required fields should have a value selected.
   */
  required?: boolean
  /**
   * Whether to use 12-hour or 24-hour time format.
   *
   * @default 12
   *
   * @example
   * <TimeInput hourCycle={24} /> // 24-hour format (e.g., 14:30)
   * <TimeInput hourCycle={12} /> // 12-hour format (e.g., 2:30 PM)
   */
  hourCycle?: 12 | 24
  /**
   * The granularity of the time field.
   *
   * Determines which time components are editable.
   *
   * @default 'minute'
   *
   * @example
   * <TimeInput granularity="hour" /> // Only hours
   * <TimeInput granularity="minute" /> // Hours and minutes
   */
  granularity?: 'hour' | 'minute'
  /**
   * The minimum allowed time.
   *
   * Times before this value cannot be selected.
   *
   * @example
   * // Business hours: no times before 9 AM
   * <TimeInput min={new Temporal.PlainTime(9, 0)} />
   */
  min?: Temporal.PlainTime
  /**
   * The maximum allowed time.
   *
   * Times after this value cannot be selected.
   *
   * @example
   * // Business hours: no times after 5 PM
   * <TimeInput max={new Temporal.PlainTime(17, 0)} />
   */
  max?: Temporal.PlainTime
  /**
   * The placeholder value shown when no time is selected.
   */
  placeholder?: Temporal.PlainTime
  /**
   * Prefix element to render before the time segments.
   *
   * The prefix color defaults to `var(--color-text-subtle)` and can be
   * overridden with the `--input-affix-color` CSS custom property.
   *
   * @example
   * <TimeInput
   *   value={time}
   *   onChange={setTime}
   *   prefix={<ClockIcon size="small" />}
   * />
   */
  prefix?: React.ReactNode
  /**
   * Suffix element to render after the time segments.
   *
   * The suffix color defaults to `var(--color-text-subtle)` and can be
   * overridden with the `--input-affix-color` CSS custom property.
   *
   * @example
   * <TimeInput
   *   value={time}
   *   onChange={setTime}
   *   suffix={<Text size="sm">EST</Text>}
   * />
   */
  suffix?: React.ReactNode
  /**
   * Additional CSS class for the time field container.
   */
  className?: string
  /**
   * Size variant of the input.
   *
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large'
  /**
   * Element id for accessibility association with label.
   */
  id?: string
  /**
   * Array of times to override with custom behavior or styling.
   *
   * Each entry can disable times, add visual variants, show tooltips, or completely
   * hide times from the dropdown. Multiple behaviors can be combined for the same time.
   *
   * @example
   * // Disable a time with custom tooltip
   * <TimeInput
   *   timeOverrides={[
   *     { time: new Temporal.PlainTime(12, 0), disabled: true, tooltipText: "Lunch break" }
   *   ]}
   * />
   *
   * @example
   * // Show warning but keep selectable
   * <TimeInput
   *   timeOverrides={[
   *     {
   *       time: new Temporal.PlainTime(14, 30),
   *       variant: 'warning',
   *       tooltipText: 'This time slot is already in use'
   *     }
   *   ]}
   * />
   *
   * @example
   * // Hide a time completely
   * <TimeInput
   *   timeOverrides={[
   *     { time: new Temporal.PlainTime(17, 0), remove: true }
   *   ]}
   * />
   *
   * @example
   * // Combine: show warning AND disable
   * <TimeInput
   *   timeOverrides={[
   *     {
   *       time: new Temporal.PlainTime(10, 0),
   *       disabled: true,
   *       variant: 'warning',
   *       tooltipText: 'Unavailable - meeting in progress'
   *     }
   *   ]}
   * />
   */
  timeOverrides?: TimeOverride[]
}

/**
 * TimeInput - Accessible time input component with keyboard navigation.
 *
 * A time input field that allows users to enter and edit time values using keyboard input.
 * Each part of a time value (hour, minute, second, AM/PM) is displayed in an individually
 * editable segment that can be navigated with arrow keys and Tab.
 *
 * @example
 * import { TimeInput } from '@buffer-mono/popcorn'
 * import { Temporal } from '@js-temporal/polyfill'
 *
 * function MeetingScheduler() {
 *   const [time, setTime] = useState<Temporal.PlainTime>(
 *     new Temporal.PlainTime(9, 0)
 *   )
 *
 *   return (
 *     <TimeInput
 *       value={time}
 *       onChange={setTime}
 *       min={new Temporal.PlainTime(9, 0)}
 *       max={new Temporal.PlainTime(17, 0)}
 *       hourCycle={12}
 *     />
 *   )
 * }
 *
 * @css `--input-affix-color` — Overrides the color of prefix and suffix elements.
 * Defaults to `var(--color-text-subtle)`. Set via `className` or `style` to change
 * the affix color without affecting the input text color.
 *
 * @ref The ref type depends on the `type` prop:
 * - `type="classic"` (default) → `HTMLDivElement` (the segment container)
 * - `type="smart"` → `HTMLInputElement` (the text input)
 */
export const TimeInput = React.forwardRef<
  HTMLDivElement | HTMLInputElement,
  TimeInputProps
>(({ type = 'classic', ...props }, ref) => {
  if (type === 'smart') {
    return (
      <SmartTimeInput ref={ref as React.Ref<HTMLInputElement>} {...props} />
    )
  }
  return <ClassicTimeInput ref={ref as React.Ref<HTMLDivElement>} {...props} />
})

TimeInput.displayName = 'TimeInput'

const ClassicTimeInput = React.forwardRef<
  HTMLDivElement,
  Omit<TimeInputProps, 'type'>
>(
  (
    {
      value,
      defaultValue,
      onChange,
      disabled,
      readOnly,
      required,
      hourCycle = 12, // Default to 12-hour format to match the design
      granularity = 'minute',
      min,
      max,
      placeholder,
      className,
      size = 'medium',
      id,
      prefix,
      suffix,
      timeOverrides,
      ...otherProps
    },
    ref,
  ) => {
    // Force en-US locale for consistent AM/PM formatting without periods
    const timeLocale = 'en-US'
    const [dropdownOpen, setDropdownOpen] = React.useState(false)

    // Internal state to handle controlled/uncontrolled component
    const [internalValue, setInternalValue] = React.useState<
      Temporal.PlainTime | undefined
    >(value || defaultValue)

    const [timeIsInPast, setTimeIsInPast] = React.useState(false)

    // Update internal state when props change
    React.useEffect(() => {
      if (value !== undefined) {
        setInternalValue(value)
      }
    }, [value])

    // Effective value (controlled via props or internal state)
    const effectiveValue = value !== undefined ? value : internalValue

    // Convert Temporal API objects to React Aria's expected format
    // These must be memoized by primitive values to avoid creating new Time
    // references on every render, which triggers infinite re-render loops in
    // react-stately's useDateFieldState (it does setState during render when
    // it detects the controlled value changed by reference).
    const timeValue = React.useMemo(
      () => (effectiveValue ? plainTimeToTimeValue(effectiveValue) : undefined),
      [effectiveValue],
    )
    const timeDefaultValue = React.useMemo(
      () => (defaultValue ? plainTimeToTimeValue(defaultValue) : undefined),
      [defaultValue],
    )
    const timeMin = React.useMemo(
      () => (min ? plainTimeToTimeValue(min) : undefined),
      [min],
    )
    const timeMax = React.useMemo(
      () => (max ? plainTimeToTimeValue(max) : undefined),
      [max],
    )
    const timePlaceholder = React.useMemo(
      () => (placeholder ? plainTimeToTimeValue(placeholder) : undefined),
      [placeholder],
    )

    // Create state for time field
    const state = useTimeFieldState({
      value: timeValue,
      defaultValue: timeDefaultValue,
      onChange: (value) => {
        setTimeIsInPast(false)
        let newTime = value ? timeValueToPlainTime(value) : null
        // Clamp to min/max if defined
        if (newTime) {
          if (min && Temporal.PlainTime.compare(newTime, min) < 0) {
            setTimeIsInPast(true)
            newTime = min
          }
          if (max && Temporal.PlainTime.compare(newTime, max) > 0) {
            newTime = max
          }
        }
        // Update internal state if uncontrolled
        if (onChange === undefined) {
          setInternalValue(newTime || undefined)
        }
        // Call external onChange if provided
        else if (onChange) {
          onChange(newTime)
        }
      },
      locale: timeLocale,
      isDisabled: disabled,
      isReadOnly: readOnly,
      isRequired: required,
      hourCycle,
      granularity,
      minValue: timeMin,
      maxValue: timeMax,
      placeholderValue: timePlaceholder,
    })

    const innerRef = React.useRef<HTMLDivElement>(null)
    const mergedRef = useMergeRefs(ref, innerRef)

    const { fieldProps } = useTimeField(
      {
        'aria-label': 'Time selector',
        ...otherProps,
        value: timeValue,
        defaultValue: timeDefaultValue,
        onChange: (value) => {
          if (onChange) {
            onChange(value ? timeValueToPlainTime(value) : null)
          }
        },
        granularity,
        minValue: timeMin,
        maxValue: timeMax,
        isDisabled: disabled,
        isReadOnly: readOnly,
        isRequired: required,
        hourCycle,
        id,
      },
      state,
      innerRef,
    )

    const dropdownTimeOptions = React.useMemo(
      () =>
        generateTimeOptions(hourCycle, granularity, min, max, timeOverrides),
      [hourCycle, granularity, min, max, timeOverrides],
    )

    const isSelected = React.useCallback(
      (time: Temporal.PlainTime): boolean => {
        if (!effectiveValue) return false

        if (granularity === 'hour') {
          return effectiveValue.hour === time.hour
        } else {
          return (
            effectiveValue.hour === time.hour &&
            effectiveValue.minute === time.minute
          )
        }
      },
      [effectiveValue, granularity],
    )

    const handleTimeChange = (time: Temporal.PlainTime): void => {
      let newTime = time
      // Clamp to min/max if defined
      if (min && Temporal.PlainTime.compare(newTime, min) < 0) {
        newTime = min
      }
      if (max && Temporal.PlainTime.compare(newTime, max) > 0) {
        newTime = max
      }

      if (onChange === undefined) {
        setInternalValue(newTime)

        state.setValue(
          (plainTimeToTimeValue(newTime) as unknown as DateValue) ?? null,
        )
      } else if (onChange) {
        onChange(newTime)
      }
      setTimeIsInPast(false)
      setDropdownOpen(false)
    }

    const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (['ArrowDown', 'ArrowUp'].includes(e.key)) {
        e.stopPropagation()
      }
      if (['Enter', ' '].includes(e.key)) {
        e.preventDefault()
        e.stopPropagation()
        if (!disabled && !readOnly) {
          setDropdownOpen((open) => !open)
        }
      }
    }

    const itemRefs = React.useRef<Array<HTMLDivElement | null>>([])
    const scrollAreaRef = React.useRef<HTMLDivElement | null>(null)
    const hasAutoScrolledRef = React.useRef(false)

    // Auto-scroll to selected time when dropdown first opens
    React.useEffect(() => {
      if (
        !dropdownOpen ||
        !effectiveValue ||
        dropdownTimeOptions.length === 0
      ) {
        // reset to scroll again next render
        hasAutoScrolledRef.current = false
        return
      }

      // if we have scrolled once, do not do it again while dropdown is open
      if (hasAutoScrolledRef.current) return

      hasAutoScrolledRef.current = true

      // Find selected item index, or closest if no exact match
      let targetIndex = dropdownTimeOptions.findIndex((opt) =>
        isSelected(opt.time),
      )

      if (targetIndex === -1) {
        // Find closest time
        const currentMinutes = effectiveValue.hour * 60 + effectiveValue.minute
        let closestIndex = 0
        let smallestDiff = Infinity

        dropdownTimeOptions.forEach((option, index) => {
          const optionMinutes = option.time.hour * 60 + option.time.minute
          const diff = Math.abs(currentMinutes - optionMinutes)

          if (diff < smallestDiff) {
            smallestDiff = diff
            closestIndex = index
          }
        })

        targetIndex = closestIndex
      }

      // Scroll to center the item
      const scrollToItem = () => {
        const item = itemRefs.current[targetIndex]
        const scrollArea = scrollAreaRef.current
        if (item && scrollArea) {
          const itemCenter = item.offsetTop + item.offsetHeight / 2
          const areaCenter = scrollArea.offsetHeight / 2
          scrollArea.scrollTop = itemCenter - areaCenter
        }
      }

      requestAnimationFrame(scrollToItem)
    }, [dropdownOpen, dropdownTimeOptions, effectiveValue, isSelected])

    const handleInteractOutside = (e: HandleInteractOutsideEvent): void => {
      const clickOutsideTrigger =
        e.type === 'dismissableLayer.pointerDownOutside' &&
        !isEventOverlappingRef(e, innerRef.current)
      const focusOutsideTrigger =
        e.type === 'dismissableLayer.focusOutside' &&
        !innerRef.current?.contains(e.target as Node)

      if (clickOutsideTrigger || focusOutsideTrigger) {
        setDropdownOpen(false)
      }
    }

    return (
      <Flex direction="column" gap="2xs" fullWidth>
        <DropdownMenu open={dropdownOpen} modal={false}>
          <DropdownMenu.Trigger
            asChild
            disabled={disabled || readOnly}
            onPointerDown={(e) => {
              // Open on pointerdown: Radix's Trigger preventDefaults pointerdown
              // while closed, and click after a cancelled pointerdown is
              // unreliable — an onClick opener can silently never fire.
              if (!disabled && !readOnly && e.button === 0 && !e.ctrlKey) {
                setDropdownOpen(true)
              }
            }}
          >
            <Flex
              direction="column"
              gap="2xs"
              fullWidth
              {...fieldProps}
              ref={mergedRef}
            >
              <div
                className={clsx(
                  styles.timeField,
                  styles[size],
                  disabled && styles.disabled,
                  className,
                )}
              >
                <Flex align="center" direction="row" fullWidth>
                  {prefix && <div className={styles.prefix}>{prefix}</div>}
                  <div className={styles.timeSegmentsWrapper}>
                    {state.segments.map((segment, i) => (
                      <TimeSegment
                        key={i}
                        segment={segment}
                        state={state}
                        onKeyDown={onInputKeyDown}
                      />
                    ))}
                  </div>
                  {suffix && <div className={styles.suffix}>{suffix}</div>}
                </Flex>
              </div>
              {timeIsInPast && (
                <Text color="subtle" size="sm">
                  The time can&apos;t be in the past
                </Text>
              )}
            </Flex>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content
            className={styles.dropdown}
            side="top"
            sideOffset={10}
            loop={true}
            onEscapeKeyDown={() => {
              setDropdownOpen(false)
            }}
            onInteractOutside={handleInteractOutside}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore - onOpenAutoFocus exists on the underlying Radix primitive but is not exposed in the TypeScript types
            onOpenAutoFocus={(e: React.FocusEvent<HTMLDivElement>) => {
              // Prevent the dropdown from automatically focusing the first item
              // To keep the focus on the segment that triggered the dropdown
              e.preventDefault()
            }}
          >
            <ScrollArea className={styles.scrollArea} ref={scrollAreaRef}>
              {dropdownTimeOptions.map((option, idx) => {
                const selected = isSelected(option.time)
                const isWarning = option.variant === 'warning'
                return (
                  <ConditionalTooltip
                    key={`${option.time.hour}:${option.time.minute}-${idx}`}
                    tooltipContent={option.tooltipText}
                  >
                    <DropdownMenu.Item
                      ref={(el) => {
                        if (!itemRefs.current) return
                        itemRefs.current[idx] = el
                      }}
                      onClick={() => handleTimeChange(option.time)}
                      disabled={disabled || readOnly || option.disabled}
                      className={clsx(
                        styles.item,
                        styles.itemWrapper,
                        selected ? styles.selected : '',
                      )}
                      tabIndex={selected ? 0 : -1}
                    >
                      {/* Use a span to ensure that first child does not find an icon which overrides styles */}
                      <span>{option.label}</span>
                      {isWarning && (
                        <WarningIcon className={styles.warningIcon} />
                      )}
                    </DropdownMenu.Item>
                  </ConditionalTooltip>
                )
              })}
            </ScrollArea>
          </DropdownMenu.Content>
        </DropdownMenu>
      </Flex>
    )
  },
)

ClassicTimeInput.displayName = 'ClassicTimeInput'
